/**
 * Href decisions for the Portable Text `link` annotation (a typed-in URL).
 *
 * Pure — no React / Next imports — so the client renderer
 * (src/components/content/portable-text-renderer.tsx) and the unit tests can
 * both use it. Keep it OUT of the 'use client' renderer module: a Server
 * Component that imports and calls a plain function from a 'use client' module
 * crashes at render.
 *
 * Locale rule (the point of `resolveLinkMark`): every on-site path is handed
 * to the locale-aware next-intl `<Link>` from `@/i18n/navigation` — including
 * links the author set to "Open in new tab" (Studio defaults that ON for new
 * links). `<Link>` runs the routing's own `getPathname` (`localePrefix:
 * 'as-needed'`), so a /ko reader's new tab opens /ko/tutorials/x while English
 * stays /tutorials/x; `target="_blank"` passes straight through to the `<a>`
 * and next/link leaves a non-_self target to the browser. We deliberately do
 * NOT hand-roll a second prefix rule here.
 *
 * Exception — paths `<Link>` must NOT prefix (it would double-prefix or point
 * into the `[locale]` segment where no route exists → 404). These render as a
 * plain `<a>` with the href exactly as authored (see `isUnprefixablePath`):
 *  - already locale-prefixed (`/es/blogs/a`, `/en/tutorials/x`, own-host
 *    `https://www.theduchy.com/ja/…`) — the author pinned a language;
 *  - non-localized app routes (`/api`, `/admin`, `/studio`, `/callback`,
 *    `/sitemap.xml`, … — the shared NON_LOCALIZED_PATH_PREFIXES the proxy uses);
 *  - static files served from public/ (`/static/…`, `/logo.webp`, `/x.pdf`).
 */

import { locales } from '@/lib/i18n/config'
import { isNonLocalizedPath } from '@/lib/i18n/non-localized-paths'

/**
 * Allow-list guard for portable-text link hrefs.
 *
 * React does NOT block dangerous URL schemes on an `<a href>` —
 * `javascript:`, `data:`, and `vbscript:` URLs all pass straight
 * through and execute on click. Since rich text (and thus editor- or
 * import-supplied hrefs) renders in many fields, a malicious href
 * is a stored-XSS-on-click vector. We allow only the safe schemes
 * (`http`, `https`, `mailto`, `tel`) plus site-relative paths
 * (`/…`, `#…`, `?…`, and bare relative paths); anything else returns
 * undefined so the link renders as plain text.
 *
 * This is the ONLY sanitization the renderer needs: it never emits raw
 * HTML (no `dangerouslySetInnerHTML`), so React's automatic escaping of
 * text content already neutralizes injected markup.
 */
export function safeHref(href: string | undefined): string | undefined {
  if (!href) return undefined
  const trimmed = href.trim()
  if (!trimmed) return undefined
  // Reject any href containing ASCII control characters. Browsers strip
  // tab/newline/CR from URLs *before* resolving the scheme, so a value
  // like "java\nscript:alert(1)" would slip past the scheme check below
  // and then execute. Legitimate URLs never carry raw control chars.
  if (/[\u0000-\u001f\u007f]/.test(trimmed)) return undefined
  // Site-relative / in-page targets — no scheme to vet.
  if (
    trimmed.startsWith('/') ||
    trimmed.startsWith('#') ||
    trimmed.startsWith('?')
  ) {
    return trimmed
  }
  // If there's a scheme (text before the first ':' that looks like a
  // URL scheme), it must be on the allow-list. A ':' that appears only
  // after a '/' (e.g. a relative path "foo/bar:baz") is not a scheme.
  const schemeMatch = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(trimmed)
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase()
    const allowed = new Set(['http', 'https', 'mailto', 'tel'])
    return allowed.has(scheme) ? trimmed : undefined
  }
  // No scheme and not anchored — treat as a relative path (allowed).
  return trimmed
}

/**
 * Our own web hosts. A `link` mark authored in Sanity as an ABSOLUTE URL to any
 * of these — chiefly the pre-launch `new.theduchy.com` staging domain, which
 * outlived go-live inside old tutorial/scene bodies (support ticket #9dceb502)
 * — is rewritten to a root-relative path, so a reader is never sent off to a
 * dead/stale domain and the link uses on-site, locale-aware navigation. Genuine
 * external URLs, and relative / #anchor / mailto: hrefs, are left untouched.
 */
export const OWN_WEB_HOSTS: ReadonlySet<string> = new Set([
  'theduchy.com',
  'www.theduchy.com',
  'new.theduchy.com',
])

export function normalizeOwnHref(href: string): string {
  const trimmed = href.trim()
  // Only absolute http(s) URLs — or protocol-relative `//host/…` ones — can
  // carry one of our hostnames; leave relative, in-page (#) and mailto:/tel:
  // hrefs exactly as authored.
  const isAbsolute = /^https?:\/\//i.test(trimmed)
  const isProtocolRelative = trimmed.startsWith('//')
  if (!isAbsolute && !isProtocolRelative) return href
  try {
    const u = new URL(isProtocolRelative ? `https:${trimmed}` : trimmed)
    if (OWN_WEB_HOSTS.has(u.hostname.toLowerCase())) {
      return `${u.pathname}${u.search}${u.hash}` || '/'
    }
  } catch {
    /* malformed URL — fall through and let safeHref decide */
  }
  return href
}

// public/ files — the proxy matcher skips image extensions outright, so a
// locale-prefixed copy (`/ko/static/x.png`) never resolves. Deliberately an
// allow-list of file types rather than "has a dot": content slugs can contain
// dots (`/store/foundations-of-rope-bondage-ebook-v1.2`).
const STATIC_FILE_RE =
  /\.(?:png|jpe?g|gif|webp|avif|svg|ico|pdf|xml|xsl|txt|json|js|css|webmanifest|mp3|mp4|webm|zip)$/i

/**
 * True for a root-relative href that must be emitted verbatim (never run
 * through the locale-aware `<Link>`): already locale-prefixed, a
 * non-localized app route, or a static public/ file. Query and hash are
 * ignored for the decision.
 */
export function isUnprefixablePath(href: string): boolean {
  const pathname = href.split(/[?#]/, 1)[0] || '/'
  const first = /^\/([^/]+)/.exec(pathname)?.[1]
  if (first && (locales as readonly string[]).includes(first.toLowerCase())) return true
  if (isNonLocalizedPath(pathname)) return true
  if (pathname.startsWith('/static/') || STATIC_FILE_RE.test(pathname)) return true
  return false
}

/**
 * How to render one `link` mark:
 *  - `internal` — an on-site root-relative path (`/tutorials/x`, `/?q=1`)
 *    that is not `isUnprefixablePath`:
 *    render through the locale-aware next-intl `<Link>`, which prefixes the
 *    reader's locale. `newTab` true → pass `target="_blank"` + `rel` (and skip
 *    prefetch — the page opens elsewhere).
 *  - `anchor` — a plain `<a>` with the href exactly as resolved: genuine
 *    external URLs (always new tab), same-page `#anchors`, `?query`,
 *    `mailto:`/`tel:` (never new tab — a blank page is all it would leave),
 *    bare relative paths (resolved by the browser against the current,
 *    already-localized URL), and `isUnprefixablePath` root-relative paths
 *    (already locale-prefixed, /api-style app routes, static files).
 *  - `null` — unsafe or empty href; render the text without a link.
 */
export type LinkMarkResolution =
  | { kind: 'internal'; href: string; newTab: boolean }
  | { kind: 'anchor'; href: string; newTab: boolean }

export function resolveLinkMark(
  rawHref: string | undefined | null,
  blank?: boolean | null,
): LinkMarkResolution | null {
  if (!rawHref) return null
  const href = safeHref(normalizeOwnHref(rawHref))
  if (!href) return null

  // Protocol-relative `//other-host/…` is an off-site URL, not a site path.
  const isProtocolRelative = href.startsWith('//')
  const isExternal = /^https?:\/\//i.test(href) || isProtocolRelative
  if (href.startsWith('/') && !isProtocolRelative) {
    if (isUnprefixablePath(href)) return { kind: 'anchor', href, newTab: !!blank }
    return { kind: 'internal', href, newTab: !!blank }
  }
  const isMailOrTel = /^(mailto|tel):/i.test(href)
  return { kind: 'anchor', href, newTab: isExternal || (!!blank && !isMailOrTel) }
}
